import { useCallback, useState } from 'react';

import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';
import { trackEvent } from '@/utils/fullstory';
import { downloadFilename, downloadImageAsPng } from '@/utils/imageDownload';

import type { DigitalAd } from '../../../../bridge/types/artifact.ts';

interface UseDownloadAdResult {
	download: () => Promise<void>;
	isDownloading: boolean;
	error: string | null;
}

export const useDownloadAd = (artifact: DigitalAd): UseDownloadAdResult => {
	const userId = useUserId();
	const [isDownloading, setIsDownloading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const firstImage = artifact.content.image_urls?.[0];

	const download = useCallback(async () => {
		if (!firstImage) {
			setError('No image to download.');
			return;
		}
		setIsDownloading(true);
		setError(null);
		try {
			await downloadImageAsPng(firstImage, userId, downloadFilename(1, 'png'));
			trackEvent('Digital Ad Downloaded', { artifact_id: artifact.id });
		} catch (err) {
			setError('Failed to download ad. Please try again.');
			logger.error('Failed to download digital ad', err);
		} finally {
			setIsDownloading(false);
		}
	}, [firstImage, userId, artifact.id]);

	return { download, isDownloading, error };
};
